<script context="module">
	import { z } from "zod";

	export const Source = z
		.object({
			url: z.string(),
			external_hash: z.string().optional(),
		})
		.or(
			z.object({
				id: z.number(),
				extension: z.string(),
				origin_width: z.number(),
				origin_height: z.number(),
				url: z.string(),
				animated: z.boolean().optional(),
				title: z.string().optional(),
			})
		)
		.or(z.string().url());
</script>

<script lang="ts">
	import { onMount } from "svelte";
	import { isLocalURL } from "./includes/Media";
	import Hydrator from "./Hydrator.svelte";
	import IntersectionObserver from "./IntersectionObserver.svelte";

	type Source = z.infer<typeof Source>;

	export let src: Source;
	export let width: number | undefined = undefined;
	export let height: number | undefined = undefined;
	export let quality: number = undefined;
	export let asBackground = false;
	export let fit: "cover" | "contain" | "fill" = undefined;
	// request from server for image resize mechanism
	export let imageRequestFit: "cover" | "contain" | "fill" = undefined;
	export let loading: "lazy" | "eager" = "lazy";
	export let domElement: HTMLElement | undefined = undefined;
	export let crop: string | undefined = undefined;
	export let contrast: number = 1.05;
	export let animated: boolean = true;
	export let animatedQuality: number = 50;
	export let alt: string | undefined = "image";
	export let fetchpriority: "high" | "low" | "auto" | undefined = undefined;

	// responsive widths
	export let widths: number[] = undefined;
	export let heights: number[] = undefined;

	let url: string;

	let isWebpSupported = true;

	onMount(() => {
		isWebpSupported = (window as any).isWebpSupported;
	});

	let sources = [];
	let currently_loaded: string;
	let is_animated = false;

	let w: number;
	let h: number;

	//	Formats the resizer can produce (its `f` param). The non-webp
	//	<source> format doubles as the requested output format, so the
	//	source's own format never matters — only that we name a real one.
	const RESIZER_FORMATS = new Set(["webp", "png", "jpeg", "gif", "heic"]);

	function extensionFromURL(value: string | undefined): string | undefined {
		if (!value) {
			return undefined;
		}

		try {
			const match = /\.([a-z0-9]+)$/i.exec(
				new URL(value, "http://localhost").pathname
			);
			return match ? match[1] : undefined;
		} catch (_e) {
			return undefined;
		}
	}

	//	Regular media objects carry `extension`; stream posters (crosses,
	//	external or from our own sites) do not, which used to render
	//	`image/undefined` sources requesting `f=undefined`. Derive it from
	//	the original URL in that case, and settle for jpeg when that has none.
	function fallbackFormat(
		extension: string | undefined,
		originalURL: string | undefined
	): string {
		for (const candidate of [extension, extensionFromURL(originalURL)]) {
			const normalized = (candidate || "")
				.toLowerCase()
				.replace(/^jpg$/, "jpeg");

			if (RESIZER_FORMATS.has(normalized)) {
				return normalized;
			}
		}

		return "jpeg";
	}

	function setSource(
		src: Source,
		width: number,
		height: number,
		quality: number,
		asBackground: boolean,
		_fit: any,
		crop: any
	) {
		//	memo guard before the w/h writes: a re-run with an equivalent src
		//	(e.g. a reloaded list handing down fresh-but-identical objects)
		//	must not reset the aspect-corrected w/h computed below
		if (src) {
			let x = JSON.stringify(arguments);

			if (currently_loaded === x) {
				return;
			}

			currently_loaded = x;
		}

		w = width;
		h = height;

		if (!w && widths && widths[0]) {
			w = widths[0];
		}

		if (!h && heights && heights[0]) {
			h = heights[0];
		}

		if (src) {
			if (
				(!alt || !alt.trim().length || alt === "image") &&
				typeof src === "object" &&
				(src as any).title
			) {
				alt = (src as any).title;
			}

			let local: any;

			url = typeof src === "object" ? src.url : src;

			is_animated =
				(src as any).animated === true || (src as any).animated === 1;

			const originalURL = url;

			local = isLocalURL(src);
			url = local ? local.url : url;

			if (local) {
				let ratio = 2;

				if (local.origin_width && local.origin_height) {
					ratio = local.origin_width / local.origin_height;

					if (isNaN(ratio)) {
						ratio = 2;
					}
				}

				if ((imageRequestFit || fit || "contain") === "contain") {
					if (w && h) {
						if (ratio >= w / h) {
							h = Math.ceil(w / ratio);
						} else {
							//	w, not the width prop: w is what reaches the
							//	markup; mutating the prop was a silent no-op
							w = Math.ceil(h * ratio);
						}
					} else if (w && !h) {
						h = Math.ceil(w / ratio);
					} else if (h && !w) {
						w = Math.ceil(h * ratio);
					}
				}

				const imageProcessPars = new URLSearchParams();

				imageProcessPars.set("a", animated ? "1" : "0");

				if (contrast) {
					imageProcessPars.set("co", contrast.toString());
				}

				if (w) {
					imageProcessPars.set("w", Math.ceil(w).toString());
				}

				if (h) {
					imageProcessPars.set("h", Math.ceil(h).toString());
				}

				if (is_animated) {
					if (animatedQuality){
						imageProcessPars.set("q", animatedQuality.toString());
					}
				} else {
					if (quality){
						imageProcessPars.set("q", quality.toString());
					}
				}

				if (imageRequestFit || fit) {
					imageProcessPars.set("r", imageRequestFit || fit);
				}

				if (crop) {
					imageProcessPars.set("c", crop);
				}

				if (asBackground) {
					if (isWebpSupported && (!animated || !is_animated)) {
						imageProcessPars.set("f", "webp");
					}
				} else {
					//	locals, never the props: mutating widths/heights
					//	persists derived values on the instance, so a re-run
					//	with a new src (and a different natural ratio under
					//	fit=contain) would keep serving the OLD image's ratios
					let setWidths = widths;
					let setHeights = heights;

					if (!setWidths || !setWidths.length) {
						if (w) {
							setWidths = [
								Math.min(w, 1000),
								Math.ceil(w / 1.25),
								Math.ceil(w / 1.5),
								Math.ceil(w / 2),
							];

							//	keep every derived variant at the requested
							//	aspect ratio — a pinned h with shrinking w
							//	produces a different crop per srcset entry
							if (h && (!setHeights || !setHeights.length)) {
								setHeights = setWidths.map((dw) =>
									Math.ceil((dw * h) / w)
								);
							}
						} else {
							setWidths = [];
						}
					}

					let setSources = [];

					const fallback = fallbackFormat(local.extension, originalURL);

					let formats = [];

					// do webp only if not animated in another format
					if (fallback !== "webp" && (!is_animated || !animated)) {
						formats.push("image/webp");
					}

					formats.push(`image/${fallback}`);

					formats.map((type) => {
						setWidths.map((w, i) => {
							imageProcessPars.set("w", w.toString());

							if (setHeights && setHeights[i]) {
								imageProcessPars.set(
									"h",
									setHeights[i].toString()
								);
							}

							imageProcessPars.set("f", type.split("/").pop());

							local.url.searchParams.set(
								"p",
								imageProcessPars.toString()
							);

							setSources.push({
								type,
								srcset: `${local.url} ${w}w`,
								media: `(min-width: ${w}px)`,
							});
						});
					});

					//	the loop above leaves the narrowest variant's w/h in
					//	the params — the fallback <img> must carry the
					//	primary dimensions
					if (w) {
						imageProcessPars.set("w", Math.ceil(w).toString());
					}

					if (h) {
						imageProcessPars.set("h", Math.ceil(h).toString());
					}

					local.url.searchParams.set(
						"p",
						imageProcessPars.toString()
					);

					setSources.push({
						type: "img",
						url: local.url,
					});

					sources = setSources;
				}

				if (isWebpSupported && (!animated || !is_animated)) {
					imageProcessPars.set("f", "webp");
				}

				local.url.searchParams.set("p", imageProcessPars.toString());
				url = local.url.toString();
			}
		} else {
			url = null;
		}
	}

	$: setSource(src, width, height, quality, asBackground, fit, crop);

	let intersecting: any;
	let loaded: string;

	$: if (intersecting && loaded !== url) {
		loaded = url;
	}
</script>

<Hydrator
	props={$$props}
	component="ImageView"
	hydrate={asBackground && !__ADMIN__}
>
	{#if asBackground && url}
		<IntersectionObserver element={domElement} bind:intersecting>
			<!-- style: directives only — a dynamic style attribute is rewritten
				wholesale on update and would wipe background-image -->
			<div
				bind:this={domElement}
				class="image {fit}"
				style:width="{w}px"
				style:height="{h}px"
				style:background-image={loaded ? `url(${loaded})` : null}
			>
				<slot />
			</div>
		</IntersectionObserver>
	{:else if sources && sources.length}
		<picture
			bind:this={domElement}
			class={fit}
		>
			{#each sources as source}
				{#if source.type === "img"}
					<img
						src={source.url}
						{alt}
						width={w}
						height={h}
						{loading}
						{fetchpriority}
					/>
				{:else}
					{@html `<source type="${source.type}" srcset="${source.srcset}" media="${source.media}">`}
				{/if}
			{/each}
		</picture>
	{:else if url}
		<img class={fit} src={url} {alt} width={w} height={h} {loading} {fetchpriority} />
	{/if}
</Hydrator>

<style>
	picture,
	img {
		object-fit: contain;
		max-width: 100%;
		height: auto;
	}

	div.fill {
		background-size: cover;
		width: 100% !important;
		height: 100% !important;
	}

	.fill img {
		width: 100%;
		height: 100%;
		object-fit: fill;
	}

	picture.fill {
		object-fit: fill;
	}

	img.fill {
		object-fit: fill;
	}

	div.contain {
		background-size: contain;
		width: 100% !important;
		height: 100% !important;
	}

	.contain img {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	picture.contain {
		object-fit: contain;
	}

	img.contain {
		object-fit: contain;
	}

	div.cover {
		background-size: cover;
		width: 100% !important;
		height: 100% !important;
	}

	.cover img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	picture.cover {
		object-fit: cover;
	}

	img.cover {
		object-fit: cover;
	}

	.image {
		background-position: center center;
		background-repeat: no-repeat;
		background-size: contain;
		margin: auto;
	}
</style>
